// ─────────────────────────────────────────────────────────
// SearchSelection.js — «Вибрані» записи в результатах пошуку
//
// Читач відмічає потрібні результати (будь-яка база, будь-який пошук,
// включно з «Шукати у знайденому» та УДК) і далі працює зі списком.
// Відмітки — за парою «база + MFN» (MFN у різних базах збігаються),
// живуть у sessionStorage до закриття вкладки, окремо для кожного логіну.
// ─────────────────────────────────────────────────────────
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import { API_URL } from '../config';
import { copyText } from '../clipboard';
import { GUEST_LOGIN, addBookmarksMany } from './CabinetPage';
import CoverArt from './CoverArt';
import './SearchSelection.css';

export const SELECTION_MAX = 50;
const NOTICE_MS = 4000;
const UNDO_MS   = 6000;

export function selectionStorageKey(login) {
  return `irbis_selection_${login || GUEST_LOGIN}`;
}

const keyOf = (db, mfn) => `${String(db || '').toUpperCase()}|${mfn}`;

function readStored(storageKey) {
  try {
    const v = JSON.parse(sessionStorage.getItem(storageKey) || '[]');
    return Array.isArray(v)
      ? v.filter(i => i && i.db && Number(i.mfn) > 0).slice(0, SELECTION_MAX)
      : [];
  } catch {
    return [];
  }
}

// Мінімум даних, потрібних смузі/вікну «Вибрані» та закладкам
function toItem(book, fallbackDb) {
  return {
    db:        String(book.db || fallbackDb || '').toUpperCase(),
    mfn:       Number(book.mfn),
    title:     book.title || '',
    author:    book.author || '',
    year:      book.year || '',
    cover_url: book.cover_url || '',
    type:      book.type || '',
  };
}

// ── Посилання на підбірку: /selection/KV.123-KST.45 ──────
// Список зашифрований прямо в адресі: сервер нічого не зберігає,
// посилання не «протухає», відкривається без входу (тихий гостьовий вхід).
const SHARE_TOKEN_RE = /^([A-Za-z]+)\.(\d+)$/;

export function selectionSharePath(items) {
  return `/selection/${items.slice(0, SELECTION_MAX)
    .map(i => `${String(i.db).toUpperCase()}.${Number(i.mfn)}`).join('-')}`;
}

// null — у адресі немає підбірки (або жодного коректного запису)
export function parseSelectionShareUrl(pathname) {
  const m = pathname.match(/\/selection\/([^/?#]+)/);
  if (!m) return null;
  const seen = new Set();
  const out = [];
  for (const token of m[1].split('-')) {
    const mt = token.match(SHARE_TOKEN_RE);
    if (!mt) continue;
    const it = { db: mt[1].toUpperCase(), mfn: Number(mt[2]) };
    const k = keyOf(it.db, it.mfn);
    if (it.mfn <= 0 || seen.has(k)) continue;
    seen.add(k);
    out.push(it);
    if (out.length >= SELECTION_MAX) break;
  }
  return out.length ? out : null;
}

// Телефон — системне меню «Поділитися» (Viber, Telegram…); комп'ютер
// або браузер без navigator.share (http, старі версії) — копіювання посилання.
// → 'shared' | 'cancelled' | 'copied' | 'failed'
function shareSelectionLink(items, lang) {
  const url  = `${window.location.origin}${selectionSharePath(items)}`;
  const copy = () => copyText(url).then(ok => (ok ? 'copied' : 'failed'));
  const coarse = typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
  if (coarse && typeof navigator.share === 'function') {
    return navigator.share({ title: t(lang, 'sel_shared_title'), url })
      .then(() => 'shared')
      .catch(err => (err && err.name === 'AbortError' ? 'cancelled' : copy()));
  }
  return copy();
}

// ── Спільне для вікна «Вибрані» і сторінки «Підбірка» ────
// Обидві дії повертають повідомлення для рядка статусу (або null — нічого не показуємо)
function shareListStatus(items, lang) {
  return shareSelectionLink(items, lang).then(res => {
    if (res === 'copied') return { kind: 'ok',  text: t(lang, 'sel_link_copied') };
    if (res === 'failed') return { kind: 'err', text: t(lang, 'sel_copy_failed') };
    return null;
  });
}

// Нумерований список з шифрами — у буфер обміну
function copyListStatus(rows, lang) {
  const text = rows
    .map((r, i) => `${i + 1}. ${r.description}${r.shifr ? `\n   ${t(lang, 'sel_shifr')}: ${r.shifr}` : ''}`)
    .join('\n');
  return copyText(text).then(ok => (ok
    ? { kind: 'ok',  text: t(lang, 'sel_copied') }
    : { kind: 'err', text: t(lang, 'sel_copy_failed') }));
}

// Повідомлення {kind, text}, що саме зникає через кілька секунд
const STATUS_MS = 3500;
function useTimedStatus() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!status) return undefined;
    const id = setTimeout(() => setStatus(null), STATUS_MS);
    return () => clearTimeout(id);
  }, [status]);
  return [status, setStatus];
}

function SelStatus({ status }) {
  return (
    <div className="sel-modal-status" aria-live="polite">
      {status && (
        <span className={`sel-modal-status-msg sel-modal-status-msg--${status.kind}`}>
          <i className={`fa-solid ${status.kind === 'ok' ? 'fa-circle-check' : 'fa-circle-exclamation'} sel-modal-status-icon`} />
          {status.text}
        </span>
      )}
    </div>
  );
}

// ── Стан вибору ──────────────────────────────────────────
export function useSearchSelection(login) {
  const storageKey = selectionStorageKey(login);
  const [items, setItems]         = useState(() => readStored(storageKey));
  const [notice, setNotice]       = useState(null);   // 'limit' | null
  const [undoItems, setUndoItems] = useState(null);   // знімок перед «Очистити»

  // Зміна логіну без перемонтування — читаємо список нового читача
  const keyRef = useRef(storageKey);
  useEffect(() => {
    if (keyRef.current === storageKey) return;
    keyRef.current = storageKey;
    setItems(readStored(storageKey));
    setUndoItems(null);
  }, [storageKey]);

  useEffect(() => {
    try {
      if (items.length) sessionStorage.setItem(storageKey, JSON.stringify(items));
      else sessionStorage.removeItem(storageKey);
    } catch {}
  }, [items, storageKey]);

  useEffect(() => {
    if (!notice) return undefined;
    const id = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (!undoItems) return undefined;
    const id = setTimeout(() => setUndoItems(null), UNDO_MS);
    return () => clearTimeout(id);
  }, [undoItems]);

  const selectedKeys = useMemo(() => new Set(items.map(i => keyOf(i.db, i.mfn))), [items]);

  const isSelected = useCallback(
    (book, fallbackDb) => selectedKeys.has(keyOf(book.db || fallbackDb, book.mfn)),
    [selectedKeys]
  );

  const toggle = useCallback((book, fallbackDb) => {
    const k = keyOf(book.db || fallbackDb, book.mfn);
    setUndoItems(null);
    if (selectedKeys.has(k)) {
      setItems(prev => prev.filter(i => keyOf(i.db, i.mfn) !== k));
      return;
    }
    if (items.length >= SELECTION_MAX) { setNotice('limit'); return; }
    setItems(prev => [...prev, toItem(book, fallbackDb)]);
  }, [items.length, selectedKeys]);

  // Вибрати/зняти всі записи поточної сторінки
  const setPage = useCallback((books, fallbackDb, select) => {
    setUndoItems(null);
    if (!select) {
      const pageKeys = new Set(books.map(b => keyOf(b.db || fallbackDb, b.mfn)));
      setItems(prev => prev.filter(i => !pageKeys.has(keyOf(i.db, i.mfn))));
      return;
    }
    const toAdd = books.filter(b => !selectedKeys.has(keyOf(b.db || fallbackDb, b.mfn)));
    const room  = SELECTION_MAX - items.length;
    if (toAdd.length > room) setNotice('limit');
    if (room <= 0) return;
    setItems(prev => [...prev, ...toAdd.slice(0, room).map(b => toItem(b, fallbackDb))]);
  }, [items.length, selectedKeys]);

  const remove = useCallback((db, mfn) => {
    const k = keyOf(db, mfn);
    setItems(prev => prev.filter(i => keyOf(i.db, i.mfn) !== k));
  }, []);

  const clear = useCallback(() => {
    if (!items.length) return;
    setUndoItems(items);
    setItems([]);
  }, [items]);

  const undo = useCallback(() => {
    if (!undoItems) return;
    setItems(undoItems);
    setUndoItems(null);
  }, [undoItems]);

  return {
    items, count: items.length, isSelected, toggle, setPage, remove,
    clear, undo, canUndo: !!undoItems, notice,
  };
}

// ── Чекбокс на картці результату ─────────────────────────
// Уся картка — кнопка «відкрити видання», тому клік і клавіші
// (Enter/пробіл) тут не повинні спливати до неї.
export function SelectCheckbox({ checked, onToggle, label, className = '' }) {
  const stop = e => e.stopPropagation();
  return (
    <label className={`sel-check ${className}`} title={label} onClick={stop} onKeyDown={stop}>
      <input type="checkbox" className="sel-check-input"
        checked={checked} onChange={onToggle} aria-label={label} />
      <span className="sel-check-box" aria-hidden="true">
        <i className="fa-solid fa-check sel-check-icon" />
      </span>
    </label>
  );
}

// ── «Вибрати всі на сторінці» ────────────────────────────
// state: 'none' | 'some' | 'all'
export function SelectPageToggle({ state, onChange, lang }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <label className="sel-page-toggle">
      <input ref={ref} type="checkbox" className="sel-check-input"
        checked={state === 'all'} onChange={() => onChange(state !== 'all')} />
      <span className={`sel-check-box${state === 'some' ? ' sel-check-box--some' : ''}`} aria-hidden="true">
        <i className={`fa-solid ${state === 'some' ? 'fa-minus' : 'fa-check'} sel-check-icon`} />
      </span>
      <span className="sel-page-toggle-text">{t(lang, 'sel_select_page')}</span>
    </label>
  );
}

// ── Вікно «Вибрані документи» ────────────────────────────
// Короткий опис із шифром — selection.php (одним запитом на весь список).
// Поки опис не прийшов (або сервер недоступний) — показуємо скорочений
// з уже відомих даних, щоб друк/копіювання працювали завжди.
function fallbackDescription(it) {
  const parts = [it.author ? `${it.author}${/\.$/.test(it.author) ? '' : '.'} ` : ''];
  parts.push(it.title || '');
  if (it.year) parts.push(`, ${it.year}`);
  const s = parts.join('').trim();
  return /[.!?]$/.test(s) ? s : `${s}.`;
}

// Мініатюра обкладинки; немає URL або картинка не завантажилась —
// векторна заглушка за типом видання (той самий CoverArt, що в результатах)
function SelCover({ item }) {
  const [failed, setFailed] = useState(false);
  const showImg = !!item.cover_url && !failed;
  return (
    <div className={`sel-modal-item-cover${showImg ? '' : ' cover-art-host'}`} aria-hidden="true">
      {showImg
        ? <img src={item.cover_url} alt="" className="sel-modal-item-cover-img"
            onError={() => setFailed(true)} />
        : <CoverArt db={item.db} type={item.type} />}
    </div>
  );
}

// Рядок списку: обкладинка, опис (відкриває видання), шифр;
// кнопка «Прибрати» — лише коли передано onRemove (у вікні «Вибрані»)
function SelListItem({ row, lang, onOpen, onRemove }) {
  return (
    <li className="sel-modal-item">
      <SelCover key={row.cover_url || 'no-cover'} item={row} />
      <div className="sel-modal-item-body">
        <button type="button" className="sel-modal-item-desc"
          onClick={() => onOpen(row)} title={t(lang, 'sel_open')}>
          {row.description}
        </button>
        {row.shifr && (
          <div className="sel-modal-item-shifr">
            {t(lang, 'sel_shifr')}: <span className="sel-modal-item-shifr-val">{row.shifr}</span>
          </div>
        )}
      </div>
      {onRemove && (
        <button type="button" className="sel-modal-item-remove"
          onClick={() => onRemove(row.db, row.mfn)}
          aria-label={`${t(lang, 'sel_remove')}: ${row.title}`} title={t(lang, 'sel_remove')}>
          <i className="fa-solid fa-trash-can" />
        </button>
      )}
    </li>
  );
}

export function SelectionModal({ open, items, onRemove, onOpenBook, onClose, lang, login, user }) {
  const [briefs,   setBriefs]   = useState({});      // key → {description, shifr}
  const [loading,  setLoading]  = useState(false);
  const [failed,   setFailed]   = useState(false);
  const [status,   setStatus]   = useTimedStatus();  // {kind, text}
  const [bmSaving, setBmSaving] = useState(false);
  const closeBtnRef = useRef(null);
  // Актуальний onClose без перезапуску ефектів (інакше фокус «стрибав» би
  // на «Закрити» при кожному рендері батька з новою стрілковою функцією)
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Довантажуємо описи лише для записів, яких ще немає в кеші.
  // briefs свідомо не в залежностях — інакше кожна відповідь перезапускала б ефект.
  useEffect(() => {
    if (!open) return undefined;
    const missing = items.filter(i => !briefs[keyOf(i.db, i.mfn)]);
    if (!missing.length) { setLoading(false); return undefined; }
    const ctrl = new AbortController();
    setLoading(true);
    setFailed(false);
    fetch(`${API_URL}/selection.php?action=brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: missing.map(i => ({ db: i.db, mfn: i.mfn })) }),
      signal: ctrl.signal,
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => {
        const next = {};
        (d.records || []).forEach(rec => {
          next[keyOf(rec.db, rec.mfn)] = { description: rec.description, shifr: rec.shifr };
        });
        // Запис, якого сервер не віддав (видалений), — більше не перепитуємо
        missing.forEach(i => {
          const k = keyOf(i.db, i.mfn);
          if (!next[k]) next[k] = { description: '', shifr: '' };
        });
        setBriefs(prev => ({ ...prev, ...next }));
        setLoading(false);
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setFailed(true);
        setLoading(false);
      });
    return () => ctrl.abort();
  }, [open, items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Esc, блокування прокрутки сторінки, фокус на «Закрити»
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => { if (e.key === 'Escape') onCloseRef.current(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // На мобілі піднімає кнопку доступності над панеллю дій (CSS)
    document.body.classList.add('sel-modal-open');
    closeBtnRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      document.body.classList.remove('sel-modal-open');
    };
  }, [open]);

  // Список порожній (усе прибрали) — вікно зайве
  useEffect(() => {
    if (open && items.length === 0) onCloseRef.current();
  }, [open, items.length]);

  const rows = useMemo(() => items.map(it => {
    const b = briefs[keyOf(it.db, it.mfn)];
    return {
      ...it,
      description: (b && b.description) || fallbackDescription(it),
      shifr: (b && b.shifr) || '',
    };
  }), [items, briefs]);

  if (!open || !items.length) return null;

  const busy = loading && !failed;

  const handleCopy  = () => { copyListStatus(rows, lang).then(setStatus); };
  const handleShare = () => { shareListStatus(items, lang).then(st => { if (st) setStatus(st); }); };

  const handlePrint = () => {
    const done = () => {
      document.body.classList.remove('sel-printing');
      window.removeEventListener('afterprint', done);
    };
    window.addEventListener('afterprint', done);
    document.body.classList.add('sel-printing');
    window.print();
  };

  const handleBookmarks = () => {
    setBmSaving(true);
    addBookmarksMany(login || GUEST_LOGIN, user, items.map(({ type, ...rest }) => rest))
      .then(n => setStatus({ kind: 'ok', text: `${t(lang, 'sel_bm_saved')}: ${n}` }))
      .catch(() => setStatus({ kind: 'err', text: t(lang, 'sel_bm_failed') }))
      .finally(() => setBmSaving(false));
  };

  const today = new Date().toLocaleDateString('uk-UA');

  return createPortal(
    <>
      <div className="sel-modal-backdrop" onClick={onClose}>
        <div className="sel-modal" role="dialog" aria-modal="true" aria-labelledby="sel-modal-title"
          onClick={e => e.stopPropagation()}>
          <div className="sel-modal-header">
            <h2 className="sel-modal-title" id="sel-modal-title">
              <i className="fa-solid fa-list-check sel-modal-title-icon" />
              {t(lang, 'sel_modal_title')}
              <span className="sel-modal-title-count">{items.length}</span>
            </h2>
            <button type="button" ref={closeBtnRef} className="sel-modal-close"
              onClick={onClose} aria-label={t(lang, 'sel_close')} title={t(lang, 'sel_close')}>
              <i className="fa-solid fa-xmark" />
            </button>
          </div>

          {(busy || failed) && (
            <div className={`sel-modal-note${failed ? ' sel-modal-note--warn' : ''}`} role="status">
              <i className={`fa-solid ${failed ? 'fa-triangle-exclamation' : 'fa-circle-notch fa-spin'} sel-modal-note-icon`} />
              {t(lang, failed ? 'sel_load_failed' : 'sel_loading')}
            </div>
          )}

          <ol className="sel-modal-list">
            {rows.map(r => (
              <SelListItem key={keyOf(r.db, r.mfn)} row={r} lang={lang} onOpen={onOpenBook} onRemove={onRemove} />
            ))}
          </ol>

          <div className="sel-modal-footer">
            <SelStatus status={status} />
            <div className="sel-modal-actions">
              <button type="button" className="sel-bar-btn" onClick={handleShare}
                title={t(lang, 'sel_share_hint')}>
                <i className="fa-solid fa-share-nodes sel-bar-btn-icon" />
                <span className="sel-bar-btn-label">{t(lang, 'sel_share')}</span>
              </button>
              <button type="button" className="sel-bar-btn" onClick={handlePrint} disabled={busy}
                title={t(lang, 'sel_print_hint')}>
                <i className="fa-solid fa-print sel-bar-btn-icon" />
                <span className="sel-bar-btn-label">{t(lang, 'sel_print')}</span>
              </button>
              <button type="button" className="sel-bar-btn" onClick={handleCopy} disabled={busy}>
                <i className="fa-regular fa-copy sel-bar-btn-icon" />
                <span className="sel-bar-btn-label">{t(lang, 'sel_copy')}</span>
              </button>
              <button type="button" className="sel-bar-btn sel-bar-btn--primary" onClick={handleBookmarks}
                disabled={bmSaving}>
                <i className={`fa-solid ${bmSaving ? 'fa-circle-notch fa-spin' : 'fa-bookmark'} sel-bar-btn-icon`} />
                <span className="sel-bar-btn-label">{t(lang, 'sel_to_bookmarks')}</span>
              </button>
              {/* Лише на мобілі: вікно там на весь екран, хрестик угорі далеко від пальця */}
              <button type="button" className="sel-bar-btn sel-modal-back-btn" onClick={onClose}>
                <i className="fa-solid fa-arrow-left sel-bar-btn-icon" />
                <span className="sel-bar-btn-label">{t(lang, 'back')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Друкована версія — бачна тільки під час друку (body.sel-printing) */}
      <div className="sel-print-root" aria-hidden="true">
        <h1 className="sel-print-title">{t(lang, 'sel_print_title')}</h1>
        <p className="sel-print-meta">{t(lang, 'sel_print_source')} · {today}</p>
        <ol className="sel-print-list">
          {rows.map(r => (
            <li key={keyOf(r.db, r.mfn)} className="sel-print-item">
              {r.description}
              {r.shifr && <div className="sel-print-shifr">{t(lang, 'sel_shifr')}: {r.shifr}</div>}
            </li>
          ))}
        </ol>
      </div>
    </>,
    document.body
  );
}

// ── Сторінка «Підбірка» (відкрита за посиланням) ─────────
// items — лише {db, mfn} з адреси; опис, обкладинку й тип дає selection.php.
// Видалені з каталогу записи сервер пропускає — показуємо, скільки їх.
// Кеш відповідей на час життя сторінки: повернення з картки видання
// на підбірку не перепитує сервер і не блимає завантаженням
const _sharedCache = new Map();

export function SharedSelectionPage({ items, lang, isSelected, selectedCount, onAddAll, onOpenBook, onBack, backLabel }) {
  const itemsKey = items.map(i => keyOf(i.db, i.mfn)).join(',');
  const [records, setRecords] = useState(() => _sharedCache.get(itemsKey) || null); // null — ще вантажиться
  const [failed,  setFailed]  = useState(false);
  const [reload,  setReload]  = useState(0);
  const [status,  setStatus]  = useTimedStatus(); // {kind, text}

  // items — через itemsKey: новий масив з тим самим вмістом не перезапускає запит
  useEffect(() => {
    const cached = _sharedCache.get(itemsKey);
    if (cached) { setRecords(cached); setFailed(false); return undefined; }
    const ctrl = new AbortController();
    setRecords(null);
    setFailed(false);
    fetch(`${API_URL}/selection.php?action=brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: items.map(i => ({ db: i.db, mfn: i.mfn })) }),
      signal: ctrl.signal,
    })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(d => {
        const recs = Array.isArray(d.records) ? d.records : [];
        _sharedCache.set(itemsKey, recs);
        setRecords(recs);
      })
      .catch(err => { if (err.name !== 'AbortError') setFailed(true); });
    return () => ctrl.abort();
  }, [itemsKey, reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = records === null && !failed;
  const missing = records ? items.length - records.length : 0;

  const handleAdd = () => {
    const fresh = records.filter(r => !isSelected(r));
    if (!fresh.length) { setStatus({ kind: 'ok', text: t(lang, 'sel_shared_already') }); return; }
    const room = SELECTION_MAX - selectedCount;
    if (room <= 0) { setStatus({ kind: 'err', text: `${t(lang, 'sel_limit')} ${SELECTION_MAX}` }); return; }
    onAddAll(records);
    const n = Math.min(fresh.length, room);
    setStatus(n < fresh.length
      ? { kind: 'err', text: `${t(lang, 'sel_shared_added')}: ${n}. ${t(lang, 'sel_limit')} ${SELECTION_MAX}` }
      : { kind: 'ok',  text: `${t(lang, 'sel_shared_added')}: ${n}` });
  };

  const handleCopy  = () => { copyListStatus(records, lang).then(setStatus); };
  const handleShare = () => { shareListStatus(items, lang).then(st => { if (st) setStatus(st); }); };

  return (
    <div className="sel-shared">
      <button type="button" className="sel-bar-btn sel-shared-back" onClick={onBack}>
        <i className="fa-solid fa-arrow-left sel-bar-btn-icon" />
        <span className="sel-bar-btn-label">{backLabel}</span>
      </button>

      <div className="sel-shared-card">
        <div className="sel-shared-header">
          <h1 className="sel-shared-title">
            <i className="fa-solid fa-share-nodes sel-shared-title-icon" />
            {t(lang, 'sel_shared_title')}
            {records && records.length > 0 && <span className="sel-modal-title-count">{records.length}</span>}
          </h1>
          <p className="sel-shared-hint">
            <i className="fa-solid fa-circle-info sel-shared-hint-icon" aria-hidden="true" />
            <span className="sel-shared-hint-text">{t(lang, 'sel_shared_hint')}</span>
          </p>
        </div>

        {loading && (
          <div className="sel-modal-note" role="status">
            <i className="fa-solid fa-circle-notch fa-spin sel-modal-note-icon" />
            {t(lang, 'sel_loading')}
          </div>
        )}
        {failed && (
          <div className="sel-modal-note sel-modal-note--warn" role="alert">
            <i className="fa-solid fa-triangle-exclamation sel-modal-note-icon" />
            {t(lang, 'sel_shared_failed')}
            <button type="button" className="sel-bar-btn sel-shared-retry" onClick={() => setReload(v => v + 1)}>
              <i className="fa-solid fa-rotate-right sel-bar-btn-icon" />
              <span className="sel-bar-btn-label">{t(lang, 'sel_shared_retry')}</span>
            </button>
          </div>
        )}
        {records && records.length === 0 && (
          <div className="sel-modal-note sel-modal-note--warn" role="status">
            <i className="fa-solid fa-circle-info sel-modal-note-icon" />
            {t(lang, 'sel_shared_empty')}
          </div>
        )}
        {records && records.length > 0 && missing > 0 && (
          <div className="sel-modal-note" role="status">
            <i className="fa-solid fa-circle-info sel-modal-note-icon" />
            {t(lang, 'sel_shared_missing')}: {missing}
          </div>
        )}

        {records && records.length > 0 && (
          <>
            <ol className="sel-modal-list">
              {records.map(r => (
                <SelListItem key={keyOf(r.db, r.mfn)} row={r} lang={lang} onOpen={onOpenBook} />
              ))}
            </ol>

            <div className="sel-modal-footer">
              <SelStatus status={status} />
              <div className="sel-modal-actions">
                <button type="button" className="sel-bar-btn" onClick={handleShare}
                  title={t(lang, 'sel_share_hint')}>
                  <i className="fa-solid fa-share-nodes sel-bar-btn-icon" />
                  <span className="sel-bar-btn-label">{t(lang, 'sel_share')}</span>
                </button>
                <button type="button" className="sel-bar-btn" onClick={handleCopy}>
                  <i className="fa-regular fa-copy sel-bar-btn-icon" />
                  <span className="sel-bar-btn-label">{t(lang, 'sel_copy')}</span>
                </button>
                <button type="button" className="sel-bar-btn sel-bar-btn--primary" onClick={handleAdd}>
                  <i className="fa-solid fa-list-check sel-bar-btn-icon" />
                  <span className="sel-bar-btn-label">{t(lang, 'sel_shared_add')}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Смуга внизу екрана ───────────────────────────────────
// Портал у body: фільтри доступності (.a11y-invert-scope) ламають
// position:fixed у нащадків — той самий прийом, що в інших плаваючих елементах.
export function SelectionBar({ count, notice, canUndo, onClear, onUndo, lang, children }) {
  const visible = count > 0 || canUndo;

  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add('has-selection-bar');
    return () => document.body.classList.remove('has-selection-bar');
  }, [visible]);

  if (!visible) return null;

  return createPortal(
    <div className="sel-bar" role="region" aria-label={t(lang, 'sel_bar_label')}>
      {notice === 'limit' && (
        <div className="sel-bar-notice" role="status">
          <i className="fa-solid fa-circle-info sel-bar-notice-icon" />
          {t(lang, 'sel_limit')} {SELECTION_MAX}
        </div>
      )}
      <div className="sel-bar-inner">
        {count === 0 ? (
          <>
            <span className="sel-bar-text" role="status">{t(lang, 'sel_cleared')}</span>
            <button type="button" className="sel-bar-btn sel-bar-btn--primary" onClick={onUndo}
              title={t(lang, 'sel_undo')} aria-label={t(lang, 'sel_undo')}>
              <i className="fa-solid fa-rotate-left sel-bar-btn-icon" />
              <span className="sel-bar-btn-label">{t(lang, 'sel_undo')}</span>
            </button>
          </>
        ) : (
          <>
            <span className="sel-bar-count" aria-live="polite">
              <i className="fa-solid fa-list-check sel-bar-count-icon" />
              {t(lang, 'sel_selected')}: <strong className="sel-bar-count-num">{count}</strong>
              <span className="sel-bar-count-max">/ {SELECTION_MAX}</span>
            </span>
            <div className="sel-bar-actions">
              {children}
              <button type="button" className="sel-bar-btn" onClick={onClear}
                title={t(lang, 'sel_clear')} aria-label={t(lang, 'sel_clear')}>
                <i className="fa-solid fa-xmark sel-bar-btn-icon" />
                <span className="sel-bar-btn-label">{t(lang, 'sel_clear')}</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
